import type Ammo from 'ammojs-typed';
import * as THREE from 'three';
import { WEGO_JOINTS } from './wegoJoints';

declare global {
  interface Window {
    Ammo?: typeof Ammo;
  }
}

type AmmoApi = Awaited<ReturnType<typeof Ammo>>;
type AmmoRigidBody = InstanceType<AmmoApi['btRigidBody']>;
type AmmoMotionState = InstanceType<AmmoApi['btDefaultMotionState']>;
type AmmoCollisionShape = InstanceType<AmmoApi['btBoxShape']>;
type AmmoConstructionInfo = InstanceType<AmmoApi['btRigidBodyConstructionInfo']>;
type AmmoTransform = InstanceType<AmmoApi['btTransform']>;
type AmmoDynamicsWorld = InstanceType<AmmoApi['btDiscreteDynamicsWorld']>;
type AmmoHingeConstraint = InstanceType<AmmoApi['btHingeConstraint']>;
type AmmoCollisionConfiguration = InstanceType<AmmoApi['btDefaultCollisionConfiguration']>;
type AmmoDispatcher = InstanceType<AmmoApi['btCollisionDispatcher']>;
type AmmoBroadphase = InstanceType<AmmoApi['btDbvtBroadphase']>;
type AmmoConstraintSolver = InstanceType<AmmoApi['btSequentialImpulseConstraintSolver']>;
type AmmoConstraintParams = AmmoApi & {
  BT_CONSTRAINT_STOP_ERP: number;
  BT_CONSTRAINT_STOP_CFM: number;
};

let ammoLoadPromise: Promise<AmmoApi> | null = null;

const loadAmmo = (): Promise<AmmoApi> => {
  ammoLoadPromise ??= new Promise<AmmoApi>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-filo-ammo]');
    const script = existing ?? document.createElement('script');
    const finish = () => {
      const bootstrap = window.Ammo;
      if (!bootstrap) {
        reject(new Error('Ammo.js global is unavailable after loading'));
        return;
      }
      void bootstrap(bootstrap).then((api) => resolve(api as AmmoApi), reject);
    };
    if (existing) {
      if (window.Ammo) finish();
      else script.addEventListener('load', finish, { once: true });
      script.addEventListener('error', () => reject(new Error('Ammo.js script failed to load')), { once: true });
      return;
    }
    script.src = '/vendor/ammo.js';
    script.async = true;
    script.dataset.filoAmmo = 'true';
    script.addEventListener('load', finish, { once: true });
    script.addEventListener('error', () => reject(new Error('Ammo.js script failed to load')), { once: true });
    document.head.appendChild(script);
  });
  return ammoLoadPromise;
};

type PhysicsBody = {
  body: AmmoRigidBody;
  mass: number;
  motionState: AmmoMotionState;
  shape: AmmoCollisionShape;
  constructionInfo: AmmoConstructionInfo;
  transform: AmmoTransform;
  object?: THREE.Object3D;
  visualObject?: THREE.Object3D;
  collisionOrigin: THREE.Vector3;
  collisionRotation: THREE.Quaternion;
  collisionOffset: THREE.Vector3;
  centerOfMassOffset: THREE.Vector3;
  initialPosition: THREE.Vector3;
  initialQuaternion: THREE.Quaternion;
};

type PhysicsJoint = {
  name: string;
  parent: string;
  child: string;
  axis: THREE.Vector3;
  object?: THREE.Object3D;
  constraint?: AmmoHingeConstraint;
};

const MOTOR_JOINTS: PhysicsJoint[] = [
  { name: 'LEFT_HIP', parent: 'BODY_link', child: 'WEGO_LEG_COVER_UPPER_L.001_link', axis: new THREE.Vector3(1, 0, 0) },
  { name: 'LEFT_ANKLE_PITCH', parent: 'WEGO_LEG_COVER_UPPER_L.001_link', child: 'WEGO_ANKLE_L.001_link', axis: new THREE.Vector3(-1, 0, 0) },
  { name: 'LEFT_ANKLE_ROLL', parent: 'WEGO_ANKLE_L.001_link', child: 'WEGO_FOOT_COVER_L.001_link', axis: new THREE.Vector3(0, 0, 1) },
  { name: 'RIGHT_HIP', parent: 'BODY_link', child: 'WEGO_LEG_COVER_UPPER_R.001_link', axis: new THREE.Vector3(1, 0, 0) },
  { name: 'RIGHT_ANKLE_PITCH', parent: 'WEGO_LEG_COVER_UPPER_R.001_link', child: 'WEGO_ANKLE_R.001_link', axis: new THREE.Vector3(-1, 0, 0) },
  { name: 'RIGHT_ANKLE_ROLL', parent: 'WEGO_ANKLE_R.001_link', child: 'WEGO_FOOT_COVER_R.001_link', axis: new THREE.Vector3(0, 0, -1) },
  { name: 'LEFT_SHOULDER', parent: 'BODY_link', child: 'NEW_WEGO_LEFT_SHOULDER_OUTSIDE.001_link', axis: new THREE.Vector3(0, -1, 0) },
  { name: 'RIGHT_SHOULDER', parent: 'BODY_link', child: 'NEW_WEGO_RIGHT_SHOULDER_OUTSIDE.001_link', axis: new THREE.Vector3(0, 1, 0) },
];

export type AmmoLinkSpec = {
  id: string;
  object: THREE.Object3D;
  debugObject?: THREE.Object3D;
  visualObject?: THREE.Object3D;
  size: THREE.Vector3;
  mass: number;
  centerOfMass?: THREE.Vector3;
};

const EXCLUDED_LINKS = new Set([
  'NEW_WEGO_HEAD_CAB.001_link',
  'NEW_WEGO_USB_COVER.001_link',
  'NEW_WEGO_HEAD_HATCH.001_link',
]);

const COLLISION_SIZES: Record<string, THREE.Vector3> = {
  BODY_link: new THREE.Vector3(0.5576, 0.8473, 0.6068),
  'NEW_WEGO_RIGHT_SHOULDER_OUTSIDE.001_link': new THREE.Vector3(0.5546, 0.2187, 0.8244),
  'NEW_WEGO_LEFT_SHOULDER_OUTSIDE.001_link': new THREE.Vector3(0.4809, 0.2275, 0.8498),
  'WEGO_LEG_COVER_UPPER_L.001_link': new THREE.Vector3(0.2321, 0.5313, 0.2207),
  'WEGO_ANKLE_L.001_link': new THREE.Vector3(0.0694, 0.208, 0.0919),
  'WEGO_FOOT_COVER_L.001_link': new THREE.Vector3(0.347, 0.1624, 0.4966),
  'WEGO_LEG_COVER_UPPER_R.001_link': new THREE.Vector3(0.249, 0.57, 0.2367),
  'WEGO_ANKLE_R.001_link': new THREE.Vector3(0.0669, 0.2005, 0.0886),
  'WEGO_FOOT_COVER_R.001_link': new THREE.Vector3(0.3487, 0.1631, 0.499),
};

const COLLISION_ORIGINS: Record<string, THREE.Vector3> = {
  BODY_link: new THREE.Vector3(0.00001, 0.02999, -0.00008),
  'NEW_WEGO_RIGHT_SHOULDER_OUTSIDE.001_link': new THREE.Vector3(-0.00026, 0.01596, -0.02201),
  'NEW_WEGO_LEFT_SHOULDER_OUTSIDE.001_link': new THREE.Vector3(-0.01267, -0.01571, -0.02628),
  'WEGO_LEG_COVER_UPPER_L.001_link': new THREE.Vector3(0.00847, -0.01662, -0.00001),
  'WEGO_ANKLE_L.001_link': new THREE.Vector3(0.00057, -0.00519, -0.00002),
  'WEGO_FOOT_COVER_L.001_link': new THREE.Vector3(0.00805, 0.00021, 0.00408),
  'WEGO_LEG_COVER_UPPER_R.001_link': new THREE.Vector3(0.00873, -0.01687, 0.00094),
  'WEGO_ANKLE_R.001_link': new THREE.Vector3(0.00055, -0.00505, -0.00145),
  'WEGO_FOOT_COVER_R.001_link': new THREE.Vector3(0.00779, 0, -0.00003),
};

const COLLISION_ROTATIONS: Record<string, THREE.Quaternion> = {
  'NEW_WEGO_RIGHT_SHOULDER_OUTSIDE.001_link': new THREE.Quaternion().setFromEuler(new THREE.Euler(0.34907, 0, 0)),
  'NEW_WEGO_LEFT_SHOULDER_OUTSIDE.001_link': new THREE.Quaternion().setFromEuler(new THREE.Euler(-0.34907, 0, 0)),
};

const FOOT_SUPPORT_OFFSET = new THREE.Vector3(0.012, 0, 0.003);
const FOOT_COM_OFFSET = new THREE.Vector3(0.015, 0, 0.004);

export const resolveFootSupportGeometry = (id: string, size: THREE.Vector3, origin: THREE.Vector3) => {
  if (id !== 'WEGO_FOOT_COVER_L.001_link' && id !== 'WEGO_FOOT_COVER_R.001_link') {
    return { size: size.clone(), origin: origin.clone() };
  }
  return {
    size: new THREE.Vector3(size.x + 0.01, size.y, size.z + 0.006),
    origin: origin.clone().add(FOOT_SUPPORT_OFFSET.clone()),
  };
};

export const getLinkCenterOfMassBias = (id: string): THREE.Vector3 => (
  id === 'WEGO_FOOT_COVER_L.001_link' || id === 'WEGO_FOOT_COVER_R.001_link'
    ? FOOT_COM_OFFSET.clone()
    : new THREE.Vector3()
);

// Bullet stores impulses as 32-bit floats; this is the largest stable value for the model scale.
const MAX_MOTOR_IMPULSE = 1_000_000;
const MAX_MOTOR_SPEED_RAD_PER_SECOND = THREE.MathUtils.degToRad(180);
const BALANCE_ASSIST_LIMIT_DEGREES = 5;
const JOINT_LOCK_SOFTNESS = 0.9;
const JOINT_LOCK_BIAS_FACTOR = 0.8;
const JOINT_LOCK_RELAXATION_FACTOR = 1;
const JOINT_LOCK_STOP_ERP = 0.8;
const JOINT_LOCK_STOP_CFM = 0;
const PHYSICS_FIXED_STEP_SECONDS = 1 / 120;
const PHYSICS_MAX_SUBSTEPS = 8;
const BALANCE_RESTORE_TILT_DEGREES = 35;
const BALANCE_RELEASE_TILT_DEGREES = 75;
const BALANCE_STIFFNESS = 480;
const BALANCE_DAMPING = 160;
const BALANCE_MAX_TORQUE = 420;
const GROUND_USER_INDEX = 1;
const LEFT_FOOT_USER_INDEX = 2;
const RIGHT_FOOT_USER_INDEX = 3;

export const toRelativeHingeRadians = (targetDegrees: number, zeroDegrees: number) =>
  THREE.MathUtils.degToRad(targetDegrees - zeroDegrees);

export const getRelativeJointLimits = (jointName: string, zeroDegrees?: number) => {
  const joint = WEGO_JOINTS[jointName as keyof typeof WEGO_JOINTS];
  const zero = zeroDegrees ?? joint?.default ?? 0;
  return joint
    ? {
        lower: toRelativeHingeRadians(joint.min, zero),
        upper: toRelativeHingeRadians(joint.max, zero),
      }
    : { lower: -Math.PI, upper: Math.PI };
};

export const hasJointTargetChange = (
  targets: ReadonlyMap<string, number>,
  zeroTargets: ReadonlyMap<string, number>,
) => [...targets].some(([name, target]) => (
  Math.abs(target - (zeroTargets.get(name) ?? target)) > 1e-4
));

export const shouldAssistBalance = (
  targets: ReadonlyMap<string, number>,
  zeroTargets: ReadonlyMap<string, number>,
) => [...targets].every(([name, target]) => (
  Math.abs(target - (zeroTargets.get(name) ?? target)) <= BALANCE_ASSIST_LIMIT_DEGREES
));

export const calculateCenterOfMass = (
  points: ReadonlyArray<{ position: THREE.Vector3; mass: number }>,
): THREE.Vector3 | null => {
  const center = new THREE.Vector3();
  let totalMass = 0;
  points.forEach(({ position, mass }) => {
    if (!Number.isFinite(mass) || mass <= 0) return;
    center.addScaledVector(position, mass);
    totalMass += mass;
  });
  return totalMass > 0 ? center.multiplyScalar(1 / totalMass) : null;
};

export const calculateFootSupportLoad = (
  footPosition: THREE.Vector3,
  centerOfMass: THREE.Vector3,
  footMass: number,
  totalMass: number,
): number => {
  if (!Number.isFinite(footMass) || footMass <= 0 || !Number.isFinite(totalMass) || totalMass <= 0) {
    return 0;
  }
  const xDistance = Math.abs(footPosition.x - centerOfMass.x);
  const proximity = 1 - THREE.MathUtils.clamp(xDistance / 0.2, 0, 1);
  const massRatio = footMass / totalMass;
  return THREE.MathUtils.clamp((proximity * 0.7) + (massRatio * 0.3), 0, 1);
};

export const calculateTiltDegrees = (
  initialQuaternion: THREE.Quaternion,
  currentQuaternion: THREE.Quaternion,
): number => {
  const initialUp = new THREE.Vector3(0, 0, 1).applyQuaternion(initialQuaternion).normalize();
  const currentUp = new THREE.Vector3(0, 0, 1).applyQuaternion(currentQuaternion).normalize();
  return THREE.MathUtils.radToDeg(Math.acos(THREE.MathUtils.clamp(initialUp.dot(currentUp), -1, 1)));
};

export const calculateBalanceTorque = (
  initialQuaternion: THREE.Quaternion,
  currentQuaternion: THREE.Quaternion,
  angularVelocity: THREE.Vector3,
  hasFootContact: boolean,
): THREE.Vector3 => {
  const tiltDegrees = calculateTiltDegrees(initialQuaternion, currentQuaternion);
  if (!hasFootContact || tiltDegrees >= BALANCE_RELEASE_TILT_DEGREES) return new THREE.Vector3();
  const initialUp = new THREE.Vector3(0, 0, 1).applyQuaternion(initialQuaternion).normalize();
  const currentUp = new THREE.Vector3(0, 0, 1).applyQuaternion(currentQuaternion).normalize();
  const restoreStrength = 1 - THREE.MathUtils.clamp(
    (tiltDegrees - BALANCE_RESTORE_TILT_DEGREES)
      / (BALANCE_RELEASE_TILT_DEGREES - BALANCE_RESTORE_TILT_DEGREES),
    0,
    1,
  );
  const torque = currentUp.cross(initialUp).multiplyScalar(BALANCE_STIFFNESS * restoreStrength)
    .addScaledVector(angularVelocity, -BALANCE_DAMPING);
  torque.z = 0;
  return torque.clampLength(0, BALANCE_MAX_TORQUE);
};

export type AmmoPhysicsDiagnostics = {
  tiltDegrees: number;
  displacement: number;
  heightDelta: number;
};

export class AmmoPhysicsWorld {
  private readonly api: AmmoApi;
  private readonly configuration: AmmoCollisionConfiguration;
  private readonly dispatcher: AmmoDispatcher;
  private readonly broadphase: AmmoBroadphase;
  private readonly solver: AmmoConstraintSolver;
  private readonly world: AmmoDynamicsWorld;
  private readonly bodies = new Map<string, PhysicsBody>();
  private readonly joints = new Map<string, PhysicsJoint>();
  private readonly jointDefinitions = MOTOR_JOINTS.map((joint) => ({
    ...joint,
    axis: joint.axis.clone(),
  }));
  private readonly jointTargets = new Map<string, number>();
  private readonly jointZeroTargets = new Map<string, number>();
  private readonly drivenJointTargets = new Map<string, number>();
  private readonly debugMeshes = new Map<string, THREE.Object3D>();
  private readonly ground: PhysicsBody;
  private centerOfMassMarker: THREE.Group | null = null;
  private rootObject: THREE.Object3D | null = null;
  private simulationStarted = false;
  private debugVisible = false;

  private constructor(api: AmmoApi) {
    this.api = api;
    this.configuration = new api.btDefaultCollisionConfiguration();
    this.dispatcher = new api.btCollisionDispatcher(this.configuration);
    this.broadphase = new api.btDbvtBroadphase();
    this.solver = new api.btSequentialImpulseConstraintSolver();
    this.world = new api.btDiscreteDynamicsWorld(
      this.dispatcher,
      this.broadphase,
      this.solver,
      this.configuration,
    );
    this.world.getSolverInfo().set_m_numIterations(80);
    const gravity = new api.btVector3(0, 0, -9.81);
    this.world.setGravity(gravity);
    api.destroy(gravity);
    this.ground = this.createBody(
      new THREE.Vector3(8, 8, 0.02),
      new THREE.Vector3(0, 0, -0.01),
      new THREE.Quaternion(),
      0,
    );
    this.ground.body.setUserIndex(GROUND_USER_INDEX);
    this.world.addRigidBody(this.ground.body, 1, 2);
  }

  static async create(): Promise<AmmoPhysicsWorld> {
    return new AmmoPhysicsWorld(await loadAmmo());
  }

  static shouldIncludeLink(id: string): boolean {
    return !EXCLUDED_LINKS.has(id);
  }

  setJointObjects(joints: Record<string, THREE.Object3D | undefined>): void {
    this.jointDefinitions.forEach((joint) => { joint.object = joints[joint.name]; });
  }

  addLink(spec: AmmoLinkSpec): void {
    if (!AmmoPhysicsWorld.shouldIncludeLink(spec.id) || this.bodies.has(spec.id)) return;
    const defaultOrigin = COLLISION_ORIGINS[spec.id] ?? new THREE.Vector3();
    const { size: collisionSize, origin: collisionOrigin } = resolveFootSupportGeometry(
      spec.id,
      COLLISION_SIZES[spec.id] ?? spec.size,
      defaultOrigin,
    );
    const collisionRotation = COLLISION_ROTATIONS[spec.id] ?? new THREE.Quaternion();
    const body = this.createBody(
      collisionSize,
      spec.object.localToWorld(collisionOrigin.clone()),
      spec.object.getWorldQuaternion(new THREE.Quaternion()).multiply(collisionRotation),
      Math.max(0, spec.mass),
    );
    body.object = spec.object;
    body.visualObject = spec.visualObject;
    body.collisionOrigin.copy(collisionOrigin);
    body.collisionRotation.copy(collisionRotation);
    body.collisionOffset.copy(collisionOrigin).multiply(spec.object.getWorldScale(new THREE.Vector3()));
    body.centerOfMassOffset
      .copy(spec.centerOfMass ?? collisionOrigin)
      .add(getLinkCenterOfMassBias(spec.id))
      .multiply(spec.object.getWorldScale(new THREE.Vector3()));
    this.bodies.set(spec.id, body);
    if (spec.id === 'WEGO_FOOT_COVER_L.001_link') body.body.setUserIndex(LEFT_FOOT_USER_INDEX);
    if (spec.id === 'WEGO_FOOT_COVER_R.001_link') body.body.setUserIndex(RIGHT_FOOT_USER_INDEX);
    this.world.addRigidBody(body.body, 2, 1);
    body.body.activate(true);
    const worldScale = spec.object.getWorldScale(new THREE.Vector3());
    const debugMesh = spec.debugObject ?? new THREE.Mesh(
      new THREE.BoxGeometry(
        Math.max(0.001, COLLISION_SIZES[spec.id]?.x ?? spec.size.x) / Math.max(1e-6, worldScale.x),
        Math.max(0.001, COLLISION_SIZES[spec.id]?.y ?? spec.size.y) / Math.max(1e-6, worldScale.y),
        Math.max(0.001, COLLISION_SIZES[spec.id]?.z ?? spec.size.z) / Math.max(1e-6, worldScale.z),
      ),
      new THREE.MeshBasicMaterial({ color: 0xff3b30, wireframe: true, depthTest: false }),
    );
    debugMesh.name = `${spec.id}_collision_box`;
    if (!spec.debugObject) {
      debugMesh.position.copy(collisionOrigin);
      debugMesh.quaternion.copy(collisionRotation);
      spec.object.add(debugMesh);
    }
    debugMesh.visible = false;
    debugMesh.frustumCulled = false;
    debugMesh.renderOrder = 20;
    this.debugMeshes.set(spec.id, debugMesh);
    this.createAvailableJoints();
  }

  setDebugVisible(visible: boolean): void {
    this.debugVisible = visible;
    this.debugMeshes.forEach((mesh) => { mesh.visible = visible; });
    if (this.centerOfMassMarker) this.centerOfMassMarker.visible = visible;
  }

  setJointTargets(targets: Record<string, number>, deltaSeconds: number): void {
    this.primeJointTargets(targets);
    this.simulationStarted ||= hasJointTargetChange(this.jointTargets, this.jointZeroTargets);
    this.applyJointTargets(deltaSeconds);
  }

  primeJointTargets(targets: Record<string, number>): void {
    Object.entries(targets).forEach(([name, target]) => {
      if (!Number.isFinite(target)) return;
      this.jointTargets.set(name, target);
      if (!this.jointZeroTargets.has(name)) this.jointZeroTargets.set(name, target);
      if (!this.drivenJointTargets.has(name)) this.drivenJointTargets.set(name, target);
    });
  }

  step(deltaSeconds: number): void {
    if (!this.simulationStarted) return;
    const clampedDelta = Math.min(1 / 20, Math.max(0, deltaSeconds));
    this.applyJointTargets(clampedDelta || 1 / 60);
    this.applyBalanceAssist();
    this.world.stepSimulation(clampedDelta, PHYSICS_MAX_SUBSTEPS, PHYSICS_FIXED_STEP_SECONDS);
  }

  private applyBalanceAssist(): void {
    const entry = this.bodies.get('BODY_link');
    if (!entry || !this.hasGroundedFoot()) return;
    const transform = entry.body.getWorldTransform();
    const rotation = transform.getRotation();
    const velocity = entry.body.getAngularVelocity();
    const torque = calculateBalanceTorque(
      entry.initialQuaternion,
      new THREE.Quaternion(rotation.x(), rotation.y(), rotation.z(), rotation.w()),
      new THREE.Vector3(velocity.x(), velocity.y(), velocity.z()),
      true,
    );
    if (torque.lengthSq() <= 0) return;
    const ammoTorque = new this.api.btVector3(torque.x, torque.y, torque.z);
    entry.body.applyTorque(ammoTorque);
    entry.body.activate(true);
    this.api.destroy(ammoTorque);
  }

  private hasGroundedFoot(): boolean {
    const manifoldCount = this.dispatcher.getNumManifolds();
    for (let manifoldIndex = 0; manifoldIndex < manifoldCount; manifoldIndex += 1) {
      const manifold = this.dispatcher.getManifoldByIndexInternal(manifoldIndex);
      const firstIndex = manifold.getBody0().getUserIndex();
      const secondIndex = manifold.getBody1().getUserIndex();
      const isFootGroundPair = (
        firstIndex === GROUND_USER_INDEX
        && (secondIndex === LEFT_FOOT_USER_INDEX || secondIndex === RIGHT_FOOT_USER_INDEX)
      ) || (
        secondIndex === GROUND_USER_INDEX
        && (firstIndex === LEFT_FOOT_USER_INDEX || firstIndex === RIGHT_FOOT_USER_INDEX)
      );
      if (!isFootGroundPair) continue;
      for (let contactIndex = 0; contactIndex < manifold.getNumContacts(); contactIndex += 1) {
        if (manifold.getContactPoint(contactIndex).getDistance() <= 0.002) return true;
      }
    }
    return false;
  }

  getDiagnostics(): AmmoPhysicsDiagnostics | null {
    const entry = this.bodies.get('BODY_link');
    if (!entry) return null;
    const transform = entry.body.getWorldTransform();
    const origin = transform.getOrigin();
    const rotation = transform.getRotation();
    const position = new THREE.Vector3(origin.x(), origin.y(), origin.z());
    const quaternion = new THREE.Quaternion(rotation.x(), rotation.y(), rotation.z(), rotation.w());
    return {
      tiltDegrees: calculateTiltDegrees(entry.initialQuaternion, quaternion),
      displacement: position.distanceTo(entry.initialPosition),
      heightDelta: position.z - entry.initialPosition.z,
    };
  }

  private applyJointTargets(deltaSeconds: number): void {
    this.joints.forEach((joint) => {
      const target = this.jointTargets.get(joint.name);
      if (target === undefined || !joint.constraint) return;
      const zeroTarget = this.jointZeroTargets.get(joint.name) ?? 0;
      const currentTarget = this.drivenJointTargets.get(joint.name) ?? target;
      const maximumDelta = THREE.MathUtils.radToDeg(MAX_MOTOR_SPEED_RAD_PER_SECOND * deltaSeconds);
      const drivenTarget = THREE.MathUtils.clamp(target, currentTarget - maximumDelta, currentTarget + maximumDelta);
      this.drivenJointTargets.set(joint.name, drivenTarget);
      const limits = getRelativeJointLimits(joint.name, zeroTarget);
      const calibratedTarget = THREE.MathUtils.clamp(
        toRelativeHingeRadians(drivenTarget, zeroTarget),
        limits.lower,
        limits.upper,
      );
      joint.constraint.setLimit(
        calibratedTarget,
        calibratedTarget,
        JOINT_LOCK_SOFTNESS,
        JOINT_LOCK_BIAS_FACTOR,
        JOINT_LOCK_RELAXATION_FACTOR,
      );
      joint.constraint.setMaxMotorImpulse(MAX_MOTOR_IMPULSE);
      joint.constraint.setMotorTarget(
        calibratedTarget,
        Math.max(1 / 120, deltaSeconds),
      );
      joint.constraint.enableMotor(true);
    });
  }

  syncTargets(rootObject?: THREE.Object3D): void {
    if (rootObject && !this.rootObject) {
      this.rootObject = rootObject;
    }
    const bodyEntry = this.bodies.get('BODY_link');
    if (!bodyEntry || !this.rootObject) return;
    const root = this.rootObject;
    root.updateWorldMatrix(true, false);
    const rootQuaternion = root.getWorldQuaternion(new THREE.Quaternion()).invert();
    const weightedPositions: Array<{ position: THREE.Vector3; mass: number }> = [];
    this.bodies.forEach((entry) => {
      if (!entry.visualObject) return;
      const transform = entry.body.getWorldTransform();
      const origin = transform.getOrigin();
      const rotation = transform.getRotation();
      const collisionPosition = new THREE.Vector3(origin.x(), origin.y(), origin.z());
      const collisionQuaternion = new THREE.Quaternion(rotation.x(), rotation.y(), rotation.z(), rotation.w());
      const linkQuaternion = collisionQuaternion.multiply(entry.collisionRotation.clone().invert());
      const linkPosition = collisionPosition.clone().sub(entry.collisionOffset.clone().applyQuaternion(linkQuaternion));
      const weightedPosition = linkPosition.clone().add(
        entry.centerOfMassOffset.clone().applyQuaternion(linkQuaternion),
      );
      weightedPositions.push({ position: weightedPosition, mass: entry.mass });
      entry.visualObject.position.copy(root.worldToLocal(linkPosition));
      entry.visualObject.quaternion.copy(rootQuaternion.clone().multiply(linkQuaternion));
    });
    const centerOfMass = calculateCenterOfMass(weightedPositions);
    if (centerOfMass) {
      this.updateCenterOfMassMarker(root, centerOfMass);
      this.updateFootSupportVisuals(centerOfMass, weightedPositions);
    }
  }

  dispose(): void {
    this.joints.forEach((joint) => {
      if (joint.constraint) {
        this.world.removeConstraint(joint.constraint);
        this.api.destroy(joint.constraint);
      }
    });
    this.joints.clear();
    this.jointTargets.clear();
    this.jointZeroTargets.clear();
    this.drivenJointTargets.clear();
    this.bodies.forEach((entry) => this.destroyBody(entry));
    this.debugMeshes.forEach((mesh) => {
      if (!(mesh as THREE.Object3D & { userData: { filoNativeCollision?: boolean } }).userData.filoNativeCollision) {
        mesh.parent?.remove(mesh);
        const debugMesh = mesh as THREE.Mesh;
        debugMesh.geometry.dispose();
        (debugMesh.material as THREE.Material).dispose();
      }
    });
    this.debugMeshes.clear();
    if (this.centerOfMassMarker) {
      this.centerOfMassMarker.parent?.remove(this.centerOfMassMarker);
      this.centerOfMassMarker.traverse((object) => {
        const mesh = object as THREE.Mesh;
        mesh.geometry?.dispose();
        if (Array.isArray(mesh.material)) mesh.material.forEach((material) => material.dispose());
        else mesh.material?.dispose();
      });
      this.centerOfMassMarker = null;
    }
    this.destroyBody(this.ground);
    this.bodies.clear();
    this.api.destroy(this.world);
    this.api.destroy(this.solver);
    this.api.destroy(this.broadphase);
    this.api.destroy(this.dispatcher);
    this.api.destroy(this.configuration);
  }

  private createBody(
    size: THREE.Vector3,
    position: THREE.Vector3,
    quaternion: THREE.Quaternion,
    mass: number,
  ): PhysicsBody {
    const origin = new this.api.btVector3(position.x, position.y, position.z);
    const rotation = new this.api.btQuaternion(quaternion.x, quaternion.y, quaternion.z, quaternion.w);
    const transform = new this.api.btTransform(rotation, origin);
    const motionState = new this.api.btDefaultMotionState(transform);
    const halfExtents = new this.api.btVector3(
      Math.max(0.001, size.x / 2),
      Math.max(0.001, size.y / 2),
      Math.max(0.001, size.z / 2),
    );
    const shape = new this.api.btBoxShape(halfExtents);
    const inertia = new this.api.btVector3(0, 0, 0);
    if (mass > 0) shape.calculateLocalInertia(mass, inertia);
    const constructionInfo = new this.api.btRigidBodyConstructionInfo(mass, motionState, shape, inertia);
    constructionInfo.set_m_friction(0.8);
    constructionInfo.set_m_restitution(0.05);
    const body = new this.api.btRigidBody(constructionInfo);
    body.setDamping(0.08, 0.12);
    body.setActivationState(4);
    this.api.destroy(inertia);
    this.api.destroy(halfExtents);
    this.api.destroy(rotation);
    this.api.destroy(origin);
    return {
      body,
      mass,
      motionState,
      shape,
      constructionInfo,
      transform,
      collisionRotation: new THREE.Quaternion(),
      collisionOffset: new THREE.Vector3(),
      collisionOrigin: new THREE.Vector3(),
      centerOfMassOffset: new THREE.Vector3(),
      initialPosition: position.clone(),
      initialQuaternion: quaternion.clone(),
    };
  }

  private updateCenterOfMassMarker(root: THREE.Object3D, worldPosition: THREE.Vector3): void {
    if (!this.centerOfMassMarker) {
      const marker = new THREE.Group();
      marker.name = 'wego_center_of_mass';
      const material = new THREE.MeshBasicMaterial({
        color: 0xffd400,
        depthTest: false,
        transparent: true,
        opacity: 0.95,
      });
      const sphere = new THREE.Mesh(new THREE.SphereGeometry(0.035, 16, 12), material);
      const ringGeometry = new THREE.TorusGeometry(0.065, 0.006, 8, 32);
      const ringX = new THREE.Mesh(ringGeometry, material.clone());
      const ringY = new THREE.Mesh(ringGeometry.clone(), material.clone());
      const ringZ = new THREE.Mesh(ringGeometry.clone(), material.clone());
      ringX.rotation.y = Math.PI / 2;
      ringY.rotation.x = Math.PI / 2;
      marker.add(sphere, ringX, ringY, ringZ);
      marker.traverse((object) => {
        object.frustumCulled = false;
        object.renderOrder = 30;
      });
      marker.visible = this.debugVisible;
      root.add(marker);
      this.centerOfMassMarker = marker;
    }
    this.centerOfMassMarker.position.copy(root.worldToLocal(worldPosition.clone()));
    const worldScale = root.getWorldScale(new THREE.Vector3());
    this.centerOfMassMarker.scale.set(
      1 / Math.max(1e-6, worldScale.x),
      1 / Math.max(1e-6, worldScale.y),
      1 / Math.max(1e-6, worldScale.z),
    );
  }

  private updateFootSupportVisuals(
    centerOfMass: THREE.Vector3,
    weightedPositions: ReadonlyArray<{ position: THREE.Vector3; mass: number }>,
  ): void {
    const totalMass = weightedPositions.reduce((sum, item) => sum + item.mass, 0);
    const footIds = ['WEGO_FOOT_COVER_L.001_link', 'WEGO_FOOT_COVER_R.001_link'];
    footIds.forEach((id) => {
      const entry = this.bodies.get(id);
      const debugMesh = this.debugMeshes.get(id);
      if (!entry || !debugMesh) return;
      const footPosition = new THREE.Vector3(
        entry.body.getWorldTransform().getOrigin().x(),
        entry.body.getWorldTransform().getOrigin().y(),
        entry.body.getWorldTransform().getOrigin().z(),
      );
      const load = calculateFootSupportLoad(footPosition, centerOfMass, entry.mass, totalMass);
      const base = id.endsWith('L.001_link') ? new THREE.Color(0x3b82f6) : new THREE.Color(0xf59e0b);
      const color = base.clone().lerp(new THREE.Color(0xff3b30), load);
      debugMesh.traverse((object) => {
        const mesh = object as THREE.Mesh;
        const material = mesh.material;
        if (!material) return;
        const materials = Array.isArray(material) ? material : [material];
        materials.forEach((candidate) => {
          if (!candidate || !('color' in candidate)) return;
          const meshMaterial = candidate as THREE.MeshBasicMaterial | THREE.MeshStandardMaterial;
          meshMaterial.color = color.clone();
          if ('opacity' in meshMaterial) {
            meshMaterial.opacity = 0.35 + (load * 0.55);
            meshMaterial.transparent = true;
          }
        });
      });
    });
  }

  private destroyBody(entry: PhysicsBody): void {
    if (!entry || !entry.body) return;
    try {
      this.world.removeRigidBody(entry.body);
    } catch {
      // Ignore stale or already-removed bodies during teardown.
    }
    try {
      this.api.destroy(entry.body);
      this.api.destroy(entry.constructionInfo);
      this.api.destroy(entry.shape);
      this.api.destroy(entry.motionState);
      this.api.destroy(entry.transform);
    } catch {
      // Ignore partially destroyed Ammo pointers during shutdown.
    }
  }

  private createAvailableJoints(): void {
    this.jointDefinitions.forEach((joint) => {
      if (this.joints.has(joint.name)) return;
      const parent = this.bodies.get(joint.parent);
      const child = this.bodies.get(joint.child);
      if (!parent || !child || !joint.object) return;

      const worldPivot = joint.object.getWorldPosition(new THREE.Vector3());
      const worldAxis = joint.axis.clone().applyQuaternion(joint.object.getWorldQuaternion(new THREE.Quaternion())).normalize();
      const pivotInParent = worldPivot.clone().sub(parent.initialPosition).applyQuaternion(parent.initialQuaternion.clone().invert());
      const pivotInChild = worldPivot.clone().sub(child.initialPosition).applyQuaternion(child.initialQuaternion.clone().invert());
      const worldFrameRotation = new THREE.Quaternion().setFromUnitVectors(
        new THREE.Vector3(0, 0, 1),
        worldAxis,
      );
      const parentFrameRotation = parent.initialQuaternion.clone().invert().multiply(worldFrameRotation);
      const childFrameRotation = child.initialQuaternion.clone().invert().multiply(worldFrameRotation);
      const parentFrameQuaternion = new this.api.btQuaternion(
        parentFrameRotation.x,
        parentFrameRotation.y,
        parentFrameRotation.z,
        parentFrameRotation.w,
      );
      const parentFrameOrigin = new this.api.btVector3(pivotInParent.x, pivotInParent.y, pivotInParent.z);
      const parentFrame = new this.api.btTransform(parentFrameQuaternion, parentFrameOrigin);
      const childFrameQuaternion = new this.api.btQuaternion(
        childFrameRotation.x,
        childFrameRotation.y,
        childFrameRotation.z,
        childFrameRotation.w,
      );
      const childFrameOrigin = new this.api.btVector3(pivotInChild.x, pivotInChild.y, pivotInChild.z);
      const childFrame = new this.api.btTransform(childFrameQuaternion, childFrameOrigin);
      const constraint = new this.api.btHingeConstraint(
        parent.body,
        child.body,
        parentFrame,
        childFrame,
        true,
      );
      const constraintParams = this.api as AmmoConstraintParams;
      constraint.setParam(constraintParams.BT_CONSTRAINT_STOP_ERP, JOINT_LOCK_STOP_ERP, -1);
      constraint.setParam(constraintParams.BT_CONSTRAINT_STOP_CFM, JOINT_LOCK_STOP_CFM, -1);
      this.api.destroy(parentFrame);
      this.api.destroy(childFrame);
      this.api.destroy(parentFrameQuaternion);
      this.api.destroy(parentFrameOrigin);
      this.api.destroy(childFrameQuaternion);
      this.api.destroy(childFrameOrigin);
      const limits = getRelativeJointLimits(joint.name);
      constraint.setLimit(limits.lower, limits.upper, 0.9, 0.3, 1);
      constraint.setMaxMotorImpulse(MAX_MOTOR_IMPULSE);
      this.world.addConstraint(constraint, true);
      joint.constraint = constraint;
      this.joints.set(joint.name, joint);
    });
  }
}
